/*
 * (C) Behaviour Interactive Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium, is strictly prohibited
 * This file is proprietary and confidential
 */

import classNames from 'classnames';
import { AnimatePresence, AnimationProps, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { Social } from '../Social';

import BehaviourLogo from '@icons/Behaviour.svg';
import FacebookIcon from '@icons/Platforms/Facebook.svg';
import InstagramIcon from '@icons/Platforms/Instagram.svg';
import LinkedinIcon from '@icons/Platforms/Linkedin.svg';
import TwitterIcon from '@icons/Platforms/Twitter-X.svg';
import YoutubeIcon from '@icons/Platforms/Youtube.svg';
import { SUPPORTED_LOCALES } from '../../translations/i18n';
import { LanguageSelector } from '../LanguageSelector';

interface Props {
  /**
   * ClassName to be applied to the footer
   */
  className?: string;
}

export function Footer({ className }: Props) {
  const { t } = useTranslation('common', { keyPrefix: 'common' });

  const footerMotionProps: AnimationProps = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 0.2, delay: 0.2 } },
    exit: { opacity: 0 },
  };

  const footerClasses = classNames(
    'z-1',
    'w-full',
    'flex flex-col gap-14 lg:gap-12',
    'text-md text-neutral-text-xlight',
    'pt-20 pb-20 lg:pb-12 px-4 lg:px-10 2xl:px-24 4xl:px-20',
    'border-t-2 border-surface-brand',
    'bg-surface-black',
    className,
  );

  const linkClasses = classNames(
    'border-b border-transparent',
    'hover:text-neutral-text-button-tertiary hover:border-b-neutral-text-button-tertiary',
    'transition-all duration-300',
  );

  return (
    <AnimatePresence>
      <motion.footer {...footerMotionProps} className={footerClasses}>
        <div className='mx-auto flex w-full max-w-nav flex-col items-center gap-14 lg:flex-row lg:items-start lg:gap-0'>
          <div className='flex flex-1 lg:mr-auto lg:justify-start'>
            <ul className='flex flex-col gap-4 text-center text-md uppercase lg:text-left'>
              <li>
                <a className={linkClasses} href={t('footer.links.about-us')} target='_blank'>
                  {t('footer.about-us')}
                </a>
              </li>
              <li>
                <a className={linkClasses} href={t('footer.links.privacy-policy')} target='_blank'>
                  {t('footer.privacy-policy')}
                </a>
              </li>
              <li>
                <a className={linkClasses} href={t('footer.links.terms-of-use')} target='_blank'>
                  {t('footer.terms-of-use')}
                </a>
              </li>
              <li>
                <a className={linkClasses} href={t('footer.links.support')} target='_blank'>
                  {t('footer.support')}
                </a>
              </li>
              {SUPPORTED_LOCALES.length > 1 && (
                <li className='m-auto normal-case lg:m-0'>
                  <LanguageSelector direction='up' />
                </li>
              )}
            </ul>
          </div>
          <div className='flex flex-1 justify-center'>
            <a href={t('bhvr.website')} target='_blank' title={t('bhvr.title')}>
              <BehaviourLogo className='h-auto w-28 xl:w-36' />
            </a>
          </div>
          <div className='flex flex-1 lg:ml-auto lg:justify-end'>
            <Social
              links={[
                {
                  icon: FacebookIcon,
                  path: 'https://www.facebook.com/behaviourinteractive/',
                  title: 'Facebook',
                },
                {
                  icon: InstagramIcon,
                  path: 'https://linkedin.com/company/behaviour-interactive/',
                  title: 'LinkedIn',
                },
                {
                  icon: LinkedinIcon,
                  path: 'https://instagram.com/behaviour_interactive',
                  title: 'Instagram',
                },
                {
                  icon: TwitterIcon,
                  path: 'https://twitter.com/behaviour',
                  title: 'X',
                },
                {
                  icon: YoutubeIcon,
                  path: 'https://www.youtube.com/channel/UCD0VJCKNdogzxUXtQPNdIiQ',
                  title: 'Youtube',
                },
              ]}
              title={t('footer.talk-games')}
            />
          </div>
        </div>
        <div className='mx-auto w-full max-w-nav text-center'>
          <p className='text-neutral-text-light'>{t('footer.copyright', { year: new Date().getFullYear() })}</p>
        </div>
      </motion.footer>
    </AnimatePresence>
  );
}
